import type { PurchasesOffering } from 'react-native-purchases';

// Load react-native-purchases LAZILY so a build without the native module
// doesn't crash on import. RevenueCat features just stay inactive until the
// next rebuild includes it.
let Purchases: any = null;
try {
  const mod = require('react-native-purchases');
  Purchases = mod?.default ?? mod;
} catch {
  Purchases = null;
}

// RevenueCat wiring. Everything here is guarded so that if the key/products
// aren't set up yet, the app simply behaves as "free" and never crashes.
//
// SETUP (one time):
//   1. Make a RevenueCat account → add your app → copy the iOS public API key.
//   2. In App Store Connect, create an auto-renewing subscription ($7.99/mo).
//   3. In RevenueCat: add that product, attach it to an Offering, and create an
//      Entitlement called "pro".
//   4. Put your key in .env:  EXPO_PUBLIC_RC_IOS_KEY=appl_xxxxxxxx
const RC_API_KEY_IOS = process.env.EXPO_PUBLIC_RC_IOS_KEY ?? '';
const ENTITLEMENT_ID = 'pro';

let configured = false;

export function configurePurchases(): void {
  if (configured || !RC_API_KEY_IOS || !Purchases) return;
  try {
    Purchases.configure({ apiKey: RC_API_KEY_IOS });
    configured = true;
  } catch (e) {
    console.error('RevenueCat configure failed:', e);
  }
}

/** True only once RevenueCat is actually set up with a key. */
export function purchasesReady(): boolean {
  return configured;
}

/** Whether the user currently has the Pro entitlement (e.g. on app launch). */
export async function isProActive(): Promise<boolean> {
  if (!configured) return false;
  try {
    const info = await Purchases.getCustomerInfo();
    return !!info.entitlements.active[ENTITLEMENT_ID];
  } catch {
    return false;
  }
}

export async function getProOffering(): Promise<PurchasesOffering | null> {
  if (!configured) return null;
  try {
    const offerings = await Purchases.getOfferings();
    return offerings.current ?? null;
  } catch {
    return null;
  }
}

// The three paywall tiers. RevenueCat's default packages carry a stable
// `packageType` ('WEEKLY' | 'MONTHLY' | 'ANNUAL') because the offering uses
// the standard $rc_weekly / $rc_monthly / $rc_annual package identifiers.
export type ProPlan = 'weekly' | 'monthly' | 'annual';
const PLAN_TO_PACKAGE_TYPE: Record<ProPlan, string> = {
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  annual: 'ANNUAL',
};

// Why a purchase attempt ended. The paywall needs to tell these apart: a
// 'cancelled' is silent (the user backed out on purpose), but 'unavailable'
// and 'error' MUST surface a message — otherwise a failed tap looks like a
// dead button ("nothing happens"), which is exactly what a missing/unapproved
// App Store product or an unconfigured RevenueCat offering produces.
export type PurchaseResult =
  | { status: 'success' }
  | { status: 'cancelled' }
  | { status: 'unavailable' } // RC not configured, or offering has no packages
  | { status: 'error'; message?: string };

/** Buy the selected plan's package (falls back to the offering's first
 *  package if the requested tier isn't found). */
export async function buyPro(plan: ProPlan = 'weekly'): Promise<PurchaseResult> {
  if (!configured) return { status: 'unavailable' };
  try {
    const offering = await getProOffering();
    const packages = offering?.availablePackages ?? [];
    const pkg =
      packages.find((p: any) => p.packageType === PLAN_TO_PACKAGE_TYPE[plan]) ??
      packages.find((p: any) => p.identifier === `$rc_${plan}`) ??
      packages[0];
    // No package means the RevenueCat offering / App Store product isn't set
    // up (or not yet approved). Surface it instead of failing silently.
    if (!pkg) {
      console.error('Purchase unavailable: offering has no packages', { plan, hasOffering: !!offering, count: packages.length });
      return { status: 'unavailable' };
    }
    const { customerInfo } = await Purchases.purchasePackage(pkg);
    if (customerInfo.entitlements.active[ENTITLEMENT_ID]) return { status: 'success' };
    // Purchase flow completed but the entitlement didn't turn on — usually an
    // entitlement/product mapping issue in RevenueCat.
    return { status: 'error', message: 'The purchase went through but Pro didn’t activate. Try Restore Purchases in a moment.' };
  } catch (e: any) {
    if (e?.userCancelled) return { status: 'cancelled' };
    console.error('Purchase failed:', e);
    return { status: 'error', message: typeof e?.message === 'string' ? e.message : undefined };
  }
}

/** Restore previous purchases (App Store requirement). Returns true if Pro. */
export async function restorePro(): Promise<boolean> {
  if (!configured) return false;
  try {
    const info = await Purchases.restorePurchases();
    return !!info.entitlements.active[ENTITLEMENT_ID];
  } catch {
    return false;
  }
}

// ── One-time "human-edited photo" purchase ──────────────────────────────────
// A single non-subscription IAP, NOT tied to the "pro" entitlement — this is a
// one-off $40 order, not recurring access. Needs its own App Store Connect
// product + RevenueCat offering (see plan doc / setup notes below); until
// that's created, getHumanPhotoPackage returns null and buyHumanPhoto reports
// 'unavailable', same as an unconfigured Pro offering does today.
//
// SETUP (one time):
//   1. App Store Connect: create a non-consumable IAP, e.g.
//      com.jaydontaro.maxifi.humanphoto, $39.99.
//   2. RevenueCat: add that product to its own offering (e.g. "humanphoto"),
//      NOT the default "pro" offering — it must not appear in the Pro paywall.
const HUMAN_PHOTO_OFFERING_ID = 'humanphoto';

async function getHumanPhotoPackage(): Promise<any | null> {
  if (!configured) return null;
  try {
    const offerings = await Purchases.getOfferings();
    const offering = offerings.all?.[HUMAN_PHOTO_OFFERING_ID] ?? null;
    return offering?.availablePackages?.[0] ?? null;
  } catch {
    return null;
  }
}

/** True if the $40 photo is genuinely purchasable right now — i.e. the
 *  `humanphoto` offering resolves to a real StoreKit package.
 *
 *  This exists so the UI can HIDE the offer until Apple has approved the IAP.
 *  Apple requires a first non-consumable to be reviewed alongside a new app
 *  version, so between shipping this code over-the-air and that review
 *  landing, the product simply doesn't exist for App Store users — showing a
 *  "$40" CTA that can only fail is worse than showing nothing. Once the IAP
 *  goes live this starts returning true on its own, no app update needed. */
export async function isHumanPhotoAvailable(): Promise<boolean> {
  return (await getHumanPhotoPackage()) != null;
}

/** True if the user has ever bought the human-edited-photo product (a
 *  non-subscription purchase, so it never expires/renews). */
export async function hasOrderedHumanPhoto(): Promise<boolean> {
  if (!configured) return false;
  try {
    const info = await Purchases.getCustomerInfo();
    const txns = info.nonSubscriptionTransactions ?? [];
    return txns.some((t: any) => (t.productIdentifier ?? '').includes('humanphoto'));
  } catch {
    return false;
  }
}

/** Buy the one-time human-edited-photo product. */
export async function buyHumanPhoto(): Promise<PurchaseResult> {
  if (!configured) return { status: 'unavailable' };
  try {
    const pkg = await getHumanPhotoPackage();
    if (!pkg) {
      console.error('Human photo purchase unavailable: no package in the "humanphoto" offering');
      return { status: 'unavailable' };
    }
    await Purchases.purchasePackage(pkg);
    return { status: 'success' };
  } catch (e: any) {
    if (e?.userCancelled) return { status: 'cancelled' };
    console.error('Human photo purchase failed:', e);
    return { status: 'error', message: typeof e?.message === 'string' ? e.message : undefined };
  }
}
